import * as THREE from 'three';
import { Entity } from './Entity.js';

/**
 * Impact particle effect entity.
 *
 * Spawns when projectiles hit something, creating a brief visual effect.
 * Particles are emitted in random directions with gravity.
 *
 * Visual:
 * - Small colored particles (default yellow)
 * - Spread in all directions
 * - Affected by gravity
 * - Fade out over lifetime
 *
 * Behavior:
 * - Lifetime of 0.3 seconds
 * - 8 particles emitted
 * - Automatically destroyed when lifetime expires
 */
export class ImpactEffect extends Entity {
  private particles: THREE.Points;
  private lifetime: number = 0.3;
  private velocities: THREE.Vector3[] = [];

  /**
   * Create a new impact effect.
   *
   * @param x - X position in world space
   * @param y - Y position in world space (will be at y=0.5 height)
   * @param color - Color of particles (default: 0xffff00 yellow)
   */
  constructor(x: number, y: number, color: number = 0xffff00) {
    super(x, y, 0);
    this.createParticles(color);
  }

  /**
   * Create the particle system.
   *
   * @param color - Color of the particles
   */
  private createParticles(color: number): void {
    const geometry = new THREE.BufferGeometry();
    const count = 8;
    const positions = new Float32Array(count * 3);

    // Initialize all particles at the impact point (height 0.5)
    for (let i = 0; i < count; i++) {
      positions[i * 3] = 0;
      positions[i * 3 + 1] = 0.5;
      positions[i * 3 + 2] = 0;

      // Generate random velocity for each particle
      const angle = Math.random() * Math.PI * 2;
      const speed = 2 + Math.random() * 3;
      this.velocities.push(
        new THREE.Vector3(
          Math.cos(angle) * speed,
          Math.random() * 2, // Upward velocity
          Math.sin(angle) * speed
        )
      );
    }

    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const material = new THREE.PointsMaterial({
      color,
      size: 0.15,
      transparent: true,
    });

    this.particles = new THREE.Points(geometry, material);
    this.mesh = this.particles;

    // Update mesh to match entity position
    this.updateMeshTransform();
  }

  /**
   * Update particle positions and lifetime.
   *
   * @param dt - Fixed delta time in milliseconds
   */
  update(dt: number): void {
    const dtSec = dt / 1000;
    this.lifetime -= dtSec;

    // Destroy when lifetime expires
    if (this.lifetime <= 0) {
      this.destroy();
      return;
    }

    // Update particle positions
    const positions = this.particles.geometry.attributes.position.array as Float32Array;
    for (let i = 0; i < this.velocities.length; i++) {
      positions[i * 3] += this.velocities[i].x * dtSec;
      positions[i * 3 + 1] += this.velocities[i].y * dtSec;
      positions[i * 3 + 2] += this.velocities[i].z * dtSec;

      // Apply gravity
      this.velocities[i].y -= 10 * dtSec;
    }
    this.particles.geometry.attributes.position.needsUpdate = true;

    // Fade out based on remaining lifetime
    const material = this.particles.material as THREE.PointsMaterial;
    material.opacity = this.lifetime / 0.3;
  }
}
